import React, { useState, useMemo } from 'react';
import { ProcedureType } from '../../../../services/ConfigurationService/types/procedureType';
import { useUpsertProcedureMutation } from '../../../../services/ConfigurationService';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Sort, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { EditProcedureDialog } from '../dialogs/EditProcedureDialog';
import { Box, Typography, TextField, Stack, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';

interface ProcedureGridRow {
  id: string;
  code: string;
  description: string;
  codeType: string;
}

export function ProcedureGrid({ procedures, centerId, onProcedureSaved }: {
  procedures: ProcedureType[];
  centerId: string;
  onProcedureSaved: () => void;
}) {
  const [editProcedure, setEditProcedure] = React.useState<ProcedureType | null>(null);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [upsertProcedure] = useUpsertProcedureMutation();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter procedures based on search term
  const filteredProcedures = useMemo(() => {
    if (!searchTerm.trim()) {
      return procedures;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return procedures.filter(procedure => 
      (procedure.code?.toLowerCase().includes(searchLower)) ||
      (procedure.description?.toLowerCase().includes(searchLower)) ||
      (procedure.codeType?.toLowerCase().includes(searchLower))
    );
  }, [procedures, searchTerm]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleRowSelection = (args: RowSelectEventArgs) => {
    if (args.data) {
      const selectedRow = args.data as ProcedureGridRow;
      const procedure = procedures.find(p => p.id === selectedRow.id);
      if (procedure) {
        setEditProcedure(procedure);
        setEditDialogOpen(true);
      }
    }
  };

  const handleDialogSave = async (procedure: ProcedureType) => {
    const id = `${centerId}-${procedure.code}`;
    await upsertProcedure({ ...procedure, centerId, id });
    setEditDialogOpen(false);
    setEditProcedure(null);
    if (onProcedureSaved) onProcedureSaved();
  };

  if (procedures.length === 0) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by code, description, or type..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No procedures found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No procedures available for this center
          </Typography>
        </Box>
      </Box>
    );
  }

  // Handle no search results
  if (filteredProcedures.length === 0 && searchTerm.trim()) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by code, description, or type..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No procedures match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: "{searchTerm}"
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Row */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SearchIcon sx={{ color: '#64748b' }} />
          <TextField
            fullWidth
            size="small"
            placeholder="Search by code, description, or type..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#3b82f6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                }
              }
            }}
          />
          {searchTerm && (
            <IconButton
              size="small"
              onClick={handleClearSearch}
              sx={{ color: '#64748b' }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Grid */}
      <GridComponent
        dataSource={filteredProcedures}
        height={350}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 25 }}
        selectionSettings={{ mode: 'Row', type: 'Single' }}
        rowSelected={handleRowSelection}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          backgroundColor: '#ffffff'
        }}
        enableHover={true}
      >
        <ColumnsDirective>
          <ColumnDirective 
            field="code" 
            headerText="Code" 
            width="120"
            textAlign="Left"
          />
          <ColumnDirective 
            field="description" 
            headerText="Description" 
            width="200"
            textAlign="Left"
          />
          <ColumnDirective 
            field="codeType" 
            headerText="Code Type" 
            width="120"
            textAlign="Left"
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
      
      {editDialogOpen && editProcedure && (
        <EditProcedureDialog
          procedure={editProcedure}
          onCancel={() => setEditDialogOpen(false)}
          onSuccess={handleDialogSave}
        />
      )}
    </Box>
  );
}


